import React from 'react'

import Stack from '@mui/material/Stack'

import { useTheme } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'

import { type BarSeriesType, BarChart } from '@mui/x-charts'

import type { BarDataProps } from '../types/Charts'
import { stripChartSlotProps } from './charts/chartSlotProps'

export interface TinyBarChartProps {
  data: BarDataProps[]
  color: (theme: Theme) => string | undefined
  width?: number
  height?: number
}

export const TinyBarChart: React.FC<TinyBarChartProps> = ({
  data,
  color,
  width = 85,
  height = 65,
}) => {
  const theme = useTheme()

  const chartData = {
    series: [
      {
        data: data.map(({ value }) => value),
        type: 'bar',
      },
    ] as BarSeriesType[],
    xAxis: [
      {
        data: data.map(({ label }) => label),
        scaleType: 'band' as const,
        categoryGapRatio: 0.3,
      },
    ],
  }

  const colorRange = data.map((_, index) =>
    index === data.length - 1 ? color(theme) : `${color(theme)}80`,
  )

  const createRoundedRectPath = (w: number, h: number) => {
    if (w <= 0 || h <= 0) return ''
    const r = Math.min(6, w / 2, h / 2)
    return `M${r},0 L${w - r},0 Q${w},0 ${w},${r} L${w},${h} L0,${h} L0,${r} Q0,0 ${r},0 Z`
  }

  return (
    <Stack
      sx={{
        width,
        height,
      }}
    >
      <BarChart
        axisHighlight={{
          x: 'none',
          y: 'none',
        }}
        height={height}
        margin={{
          bottom: -10,
          left: 0,
          right: 0,
          top: 10,
        }}
        series={chartData.series}
        slots={{
          bar: ({ ownerState, ...props }) => {
            const { ariaLabel } = data[ownerState.dataIndex]
            return (
              <g aria-label={ariaLabel}>
                <path
                  d={createRoundedRectPath(props.width, props.height)}
                  fill={colorRange[ownerState.dataIndex]}
                  transform={`translate(${props.x}, ${props.y})`}
                />
              </g>
            )
          },
          axisTickLabel: ({ text, ...restProps }) => {
            const label = data[data.length - 1]?.label
            return (
              <text
                {...stripChartSlotProps(restProps)}
                style={
                  {
                    ...restProps.style,
                    fontSize: 6.75,
                    lineHeight: '9px',
                    fontWeight: label === text ? 'bold' : 400,
                  } as React.CSSProperties
                }
              >
                {text}
              </text>
            )
          },
        }}
        sx={{
          '& .MuiChartsAxis-bottom .MuiChartsAxis-tickContainer:first-of-type .MuiChartsAxis-tick':
            {
              display: 'none',
            },
        }}
        width={width}
        xAxis={chartData.xAxis}
        yAxis={[{ width: 0, position: 'none' }]}
      />
    </Stack>
  )
}
